import { Trash2 } from "lucide-react";

import { ErrorAlert, Modal, Button } from "@/components/ui";
import type { DnsRecord } from "@/lib/domains";

import { fullHost, recordValue, touchesEmail, touchesWebsite } from "./util";

/** Figma 4082-1236 "Remove this DNS record?". `kept` is set when the removal settles a conflict. */
export function RemoveModal({
  open,
  fqdn,
  records,
  kept,
  onClose,
  onConfirm,
  removing,
  error,
}: {
  open: boolean;
  fqdn: string;
  records: DnsRecord[];
  /** The record that stays when this removal fixes a conflict. */
  kept: DnsRecord | null;
  onClose: () => void;
  onConfirm: () => void;
  removing: boolean;
  error: unknown;
}) {
  const host = records[0]?.name ?? "@";
  const where = fullHost(host, fqdn);
  const website = records.some(touchesWebsite);
  const email = records.some(touchesEmail);
  const many = records.length > 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={420}
      icon={
        <span className="flex size-10 items-center justify-center rounded-[11px] bg-[#fff1ee] text-[#d6342f]">
          <Trash2 aria-hidden className="size-5" />
        </span>
      }
      title={many ? `Remove these ${records.length} DNS records?` : "Remove this DNS record?"}
      note="This can't be undone once removed."
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" size="lg" loading={removing} onClick={onConfirm}>
            {many ? "Remove records" : "Remove record"}
          </Button>
        </>
      }
    >
      {records.map((r) => (
        <div key={r.id} className="flex flex-col gap-1 rounded-[10px] bg-[#f3ebe7] px-2.5 py-2.5">
          <p className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-fg">
            <span>{r.type}</span>
            <span>{r.name}</span>
            <span aria-hidden>→</span>
            <span className="min-w-0 break-all">{recordValue(r)}</span>
          </p>
          <p className="text-[11px] text-[#766e6a]">
            {kept
              ? `This is the conflicting record — the one being replaced, not the ${kept.type} record that stays.`
              : r.managed_by === "website"
                ? "CortexGrip added this record to connect your website."
                : `Added ${r.created_by_email ? `by ${r.created_by_email}` : "to this domain"}.`}
          </p>
        </div>
      ))}
      <p className="text-[13px] text-[#5c5551]">
        {kept
          ? `Removing ${many ? "these" : "this"} frees up ${host === "@" ? fqdn : `the ${host} host`} so the ${kept.type} record pointing to ${kept.content} can take effect.`
          : website
            ? `Removing ${many ? "these" : "this"} can stop ${where} reaching your website.`
            : email
              ? `Removing ${many ? "these" : "this"} can change how email for ${fqdn} is delivered or checked.`
              : `${where} will stop answering with ${many ? "these values" : "this value"}.`}{" "}
        Until DNS re-propagates (usually a few minutes), {where} may briefly behave differently.
      </p>
      <ErrorAlert error={error} />
    </Modal>
  );
}
